import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, FlywheelSummaryData } from '../lib/api';

const TIME_FILTER_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: '12_months', label: 'Last 12 Months' },
  { value: '6_months', label: 'Last 6 Months' },
  { value: 'quarter', label: 'This Quarter' },
];

export function FlywheelSummary() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<FlywheelSummaryData | null>(null);
  const [timeFilter, setTimeFilter] = useState<
    'all' | '12_months' | '6_months' | 'quarter'
  >('all');

  useEffect(() => {
    loadData();
  }, [timeFilter]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.getFlywheelSummary(timeFilter);
      setData(result);
    } catch (err) {
      setError('Failed to load flywheel summary');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (roadmapItemId: string) => {
    navigate(`/roadmap/${roadmapItemId}`);
  };

  const calculateLoopRate = (notified: number, clients: number) => {
    if (clients === 0) return 0;
    return Math.round((notified / clients) * 100);
  };

  const calculateAdoptionRate = (adopted: number, notified: number) => {
    if (notified === 0) return 0;
    return Math.round((adopted / notified) * 100);
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Flywheel Summary</h1>
        <p className="page-subtitle">
          Track the full cycle: Insight → Roadmap → Ship → Notify → Adopt
        </p>
      </div>

      {/* Time Filter */}
      <div className="filters-bar mb-1">
        <div className="filter-group">
          <label className="form-label">Time Period</label>
          <select
            className="form-select"
            value={timeFilter}
            onChange={(e) =>
              setTimeFilter(
                e.target.value as 'all' | '12_months' | '6_months' | 'quarter'
              )
            }
          >
            {TIME_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {data && (
        <>
          {/* Summary Stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{data.totals.shipped_count}</div>
              <div className="stat-label">Items Shipped</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{data.totals.total_insights}</div>
              <div className="stat-label">Insights Linked</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{data.totals.total_clients}</div>
              <div className="stat-label">Clients Involved</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{data.totals.total_notified}</div>
              <div className="stat-label">Clients Notified</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{data.totals.total_adopted}</div>
              <div className="stat-label">Clients Adopted</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {calculateLoopRate(
                  data.totals.total_notified,
                  data.totals.total_clients
                )}
                %
              </div>
              <div className="stat-label">Loop Close Rate</div>
            </div>
          </div>

          {/* Items Table */}
          <div className="card mt-1">
            <div className="card-header">
              <h3>Shipped Items</h3>
            </div>
            <div className="card-body">
              {data.items.length === 0 ? (
                <p className="text-muted">
                  No shipped items in selected period
                </p>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '35%' }}>Item</th>
                        <th>Shipped</th>
                        <th>Insights</th>
                        <th>Clients</th>
                        <th>Notified</th>
                        <th>Adopted</th>
                        <th>Loop Rate</th>
                        <th>Adopt Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((item) => {
                        const loopRate = calculateLoopRate(
                          item.notified_count,
                          item.client_count
                        );
                        const adoptRate = calculateAdoptionRate(
                          item.adopted_count,
                          item.notified_count
                        );
                        return (
                          <tr
                            key={item.roadmap_item_id}
                            className="insight-row"
                            onClick={() => handleRowClick(item.roadmap_item_id)}
                          >
                            <td>
                              <div className="insight-text">{item.title}</div>
                            </td>
                            <td>{item.actual_ship_date}</td>
                            <td>
                              <span
                                className={
                                  item.insight_count > 0
                                    ? 'tag tag-valid'
                                    : 'text-muted'
                                }
                              >
                                {item.insight_count}
                              </span>
                            </td>
                            <td>{item.client_count}</td>
                            <td>
                              <span
                                className={
                                  item.notified_count > 0
                                    ? 'tag tag-warning'
                                    : 'text-muted'
                                }
                              >
                                {item.notified_count}
                              </span>
                            </td>
                            <td>
                              <span
                                className={
                                  item.adopted_count > 0
                                    ? 'tag tag-valid'
                                    : 'text-muted'
                                }
                              >
                                {item.adopted_count}
                              </span>
                            </td>
                            <td>
                              <div className="progress-bar-container">
                                <div
                                  className="progress-bar"
                                  style={{
                                    width: `${loopRate}%`,
                                    background:
                                      loopRate >= 75
                                        ? 'var(--success)'
                                        : loopRate >= 50
                                          ? 'var(--warning)'
                                          : 'var(--error)',
                                  }}
                                />
                                <span className="progress-label">
                                  {loopRate}%
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="progress-bar-container">
                                <div
                                  className="progress-bar"
                                  style={{
                                    width: `${adoptRate}%`,
                                    background:
                                      adoptRate >= 75
                                        ? 'var(--success)'
                                        : adoptRate >= 50
                                          ? 'var(--warning)'
                                          : 'var(--error)',
                                  }}
                                />
                                <span className="progress-label">
                                  {adoptRate}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Insights Section */}
          <div className="card mt-1">
            <div className="card-header">
              <h3>Key Metrics Explanation</h3>
            </div>
            <div className="card-body">
              <div className="detail-grid" style={{ gap: '1rem' }}>
                <div>
                  <strong>Loop Close Rate</strong>
                  <p className="text-small text-muted">
                    Percentage of involved clients who were notified about the
                    shipped feature. Target: 75%+
                  </p>
                </div>
                <div>
                  <strong>Adoption Rate</strong>
                  <p className="text-small text-muted">
                    Percentage of notified clients who have adopted the feature.
                    Measures feature success.
                  </p>
                </div>
                <div>
                  <strong>Insights Linked</strong>
                  <p className="text-small text-muted">
                    Number of client insights that contributed to the roadmap
                    decision. Shows customer-driven development.
                  </p>
                </div>
                <div>
                  <strong>Clients Involved</strong>
                  <p className="text-small text-muted">
                    Unique clients who mentioned this need or are being tracked
                    for adoption.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
